import { Navigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDate, maskCnpjCpf, maskName } from "@/lib/format";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";
import { X, Search, FileText, Filter, ArrowLeft, ArrowRight, ArrowDownUp, ChevronDown, ChevronUp, Database, Download, Box, Activity, Zap, ShieldCheck, Layers, Gauge, History } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotaDetailModal } from "@/components/NotaDetailModal";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const PAGE = 50;

export default function NotasPage() {
  const { filteredNotas, dashboard, filtroStatus, setFiltroStatus, privacyMode, customAlerts } = useFiscalStore();
  const [q, setQ] = useState("");
  const [modelo, setModelo] = useState<"all" | "55" | "65">("all");
  const [page, setPage] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<"numero" | "emissao" | "valorOficial" | "qtdItens">("emissao");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filtroTipo, setFiltroTipo] = useState<"all" | "ENTRADA" | "SAIDA" | "INDEFINIDA">("all");

  useEffect(() => { document.title = "XML Datasets — DataGrid XML"; }, []);
  useEffect(() => { setPage(0); }, [filtroStatus, q, modelo, filtroTipo]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const arr = filteredNotas.filter((n) => {
      if (filtroStatus && n.notaStatus !== filtroStatus) return false;
      if (modelo !== "all" && String(n.modelo) !== modelo) return false;
      if (filtroTipo !== "all" && n.tipoOperacao !== filtroTipo) return false;
      if (ql && !(
        n.chave.toLowerCase().includes(ql) ||
        n.numero.toLowerCase().includes(ql) ||
        n.emitente.toLowerCase().includes(ql) ||
        n.cnpjEmit.toLowerCase().includes(ql)
      )) return false;
      return true;
    });

    return arr.sort((a, b) => {
      const factor = sortDir === "asc" ? 1 : -1;
      if (sortKey === "numero") return (parseInt(a.numero) - parseInt(b.numero)) * factor;
      if (sortKey === "emissao") return ((a.emissao || "").localeCompare(b.emissao || "")) * factor;
      if (sortKey === "valorOficial") return (a.valorOficial - b.valorOficial) * factor;
      if (sortKey === "qtdItens") return (a.qtdItens - b.qtdItens) * factor;
      return 0;
    });
  }, [filteredNotas, q, modelo, filtroStatus, filtroTipo, sortKey, sortDir]);

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const totalValueFiltered = filtered.reduce((acc, n) => acc + n.valorOficial, 0);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      {/* Forensic Header */}
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Database className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Data Integrity Dataset</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Inspeção de <span className="text-primary">XMLs</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
             <span className="flex items-center gap-2 uppercase tabular-nums"><Layers className="size-3.5 text-primary" /> {fmtNum(filtered.length)} DOCUMENTOS FILTRADOS</span>
             <span className="flex items-center gap-2 uppercase tabular-nums"><Activity className="size-3.5 text-emerald-500" /> Engine Synced</span>
          </p>
        </div>
        
        <div className="flex items-center gap-4">
           <div className="glass-technical px-6 py-3 border-primary/20 flex flex-col justify-center">
              <span className="text-[8px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Volume do Lote</span>
              <span className="text-xl font-black text-primary tabular-nums leading-none tracking-tighter">{fmtBRL(totalValueFiltered)}</span>
           </div>
           <Button variant="outline" className="h-12 rounded-2xl bg-white/5 border-foreground/5 hover:bg-primary/5 hover:border-primary/20 transition-all font-black text-[10px] uppercase tracking-widest px-6">
              <Download className="size-4 mr-2" /> Exportar CSV
           </Button>
        </div>
      </header>

      {/* Advanced Control Panel */}
      <div className="glass-card p-6 grid grid-cols-1 xl:grid-cols-12 gap-4 items-center border-primary/5 bg-muted/5">
        <div className="xl:col-span-5 relative group">
          <Search className="size-4 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <input 
            placeholder="Buscar por Chave, Número ou Emitente..." 
            value={q} 
            onChange={(e) => { setQ(e.target.value); setPage(0); }} 
            className="input-premium w-full pl-14 h-14 rounded-2xl bg-white/10 border-foreground/5 text-xs font-bold uppercase tracking-tight"
          />
        </div>

        <div className="xl:col-span-7 flex flex-wrap items-center gap-3">
          <Select
            value={filtroStatus ?? "all"}
            onValueChange={(v) => setFiltroStatus(v === "all" ? null : (v as NotaStatus))}
          >
            <SelectTrigger className="w-[180px] h-14 rounded-2xl glass-technical border-foreground/5 font-black text-[10px] uppercase tracking-widest bg-white/5">
              <SelectValue placeholder="Situação" />
            </SelectTrigger>
            <SelectContent className="glass-card">
              <SelectItem value="all">SITUAÇÃO: TODOS</SelectItem>
              <SelectItem value="AUTORIZADA">AUTORIZADAS</SelectItem>
              <SelectItem value="CONTINGENCIA">CONTINGÊNCIA</SelectItem>
              <SelectItem value="INUTILIZADA">INUTILIZADAS</SelectItem>
              <SelectItem value="CANCELADA">CANCELADAS</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filtroTipo} onValueChange={(v) => { setFiltroTipo(v as any); setPage(0); }}>
            <SelectTrigger className="w-[170px] h-14 rounded-2xl glass-technical border-foreground/5 font-black text-[10px] uppercase tracking-widest bg-white/5">
              <SelectValue placeholder="Operação" />
            </SelectTrigger>
            <SelectContent className="glass-card">
              <SelectItem value="all">OPERAÇÃO: TODAS</SelectItem>
              <SelectItem value="ENTRADA">ENTRADA (INBOUND)</SelectItem>
              <SelectItem value="SAIDA">SAÍDA (OUTBOUND)</SelectItem>
              <SelectItem value="INDEFINIDA">NÃO IDENTIFICADA</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex h-14 glass-technical border-foreground/5 rounded-2xl p-1.5 bg-white/5">
            {(["all", "55", "65"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setModelo(m); setPage(0); }}
                className={cn(
                  "px-6 h-full text-[10px] font-black uppercase tracking-[0.2em] rounded-xl transition-all",
                  modelo === m ? "bg-primary text-white shadow-xl shadow-primary/30" : "text-muted-foreground hover:bg-white/5"
                )}
              >
                {m === "all" ? "Dataset" : m === "55" ? "NF-e" : "NFC-e"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Technical Data Stream Table */}
      <div className="glass-card overflow-hidden border-primary/5">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-muted/10 text-muted-foreground border-b border-foreground/5">
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Entidade Identificada</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5 group" onClick={() => toggleSort("numero")}>
                  <div className="flex items-center gap-2">Protocolo <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5 group" onClick={() => toggleSort("emissao")}>
                  <div className="flex items-center gap-2">Data Emissão <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5 group" onClick={() => toggleSort("valorOficial")}>
                  <div className="flex items-center justify-end gap-2">Valor Total <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5 text-center">Engine Status</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-center">Tipo Fluxo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/[0.03]">
              {slice.map((n, idx) => {
                const matchesAlert = customAlerts.some(alert => {
                  if (!alert.active || !alert.value) return false;
                  const val = n[alert.field as keyof NotaSimplificada];
                  if (val === undefined || val === null) return false;
                  const target = alert.field === "valorOficial" ? parseFloat(alert.value) : alert.value;
                  const current = alert.field === "valorOficial" ? Number(val) : String(val);
                  if (alert.operator === ">") return Number(current) > Number(target);
                  if (alert.operator === "<") return Number(current) < Number(target);
                  if (alert.operator === "==") return String(current) === String(target);
                  if (alert.operator === "includes") return String(current).toLowerCase().includes(String(target).toLowerCase());
                  return false;
                });

                return (
                  <Row 
                    key={n.chave + n.numero} 
                    n={n} 
                    onClick={() => setOpenIndex(page * PAGE + idx)} 
                    privacyMode={privacyMode} 
                    highlight={matchesAlert}
                  />
                );
              })}
              {slice.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-4 opacity-10 animate-pulse">
                      <Database className="size-16" />
                      <span className="text-sm font-black uppercase tracking-[0.4em]">Zero Registers Found</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Advanced Pagination Hub */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-6 bg-muted/10 border-t border-primary/10">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-xl glass-technical border-primary/20 flex items-center justify-center text-primary">
               <History className="size-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
              Registros <span className="text-foreground">{(page * PAGE) + 1}—{Math.min((page + 1) * PAGE, filtered.length)}</span> de {fmtNum(filtered.length)}
            </span>
          </div>

          <div className="flex items-center gap-6 mt-4 sm:mt-0">
            <div className="flex flex-col items-end">
               <span className="text-[8px] font-black text-primary uppercase tracking-widest opacity-40">Navegação Dataset</span>
               <span className="text-[10px] font-black text-primary uppercase tracking-widest tabular-nums">Página {page + 1} de {totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowLeft className="size-5" />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <NotaDetailModal
        notas={filtered}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onChangeIndex={setOpenIndex}
      />
    </div>
  );
}

function Row({ n, onClick, privacyMode, highlight }: { n: NotaSimplificada; onClick: () => void; privacyMode?: boolean; highlight?: boolean }) {
  return (
    <tr 
      className={cn(
        "hover:bg-primary/[0.05] transition-all cursor-pointer group relative overflow-hidden",
        highlight && "bg-amber-500/[0.03] dark:bg-amber-500/[0.06] hover:bg-amber-500/[0.08]"
      )} 
      onClick={onClick}
    >
      {highlight && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.6)] z-10" />
      )}
      
      <td className="p-5 border-r border-foreground/[0.03]">
        <div className="flex items-center gap-4">
          <div className={cn("size-10 rounded-2xl flex items-center justify-center font-black text-[10px] border shadow-2xl transition-transform group-hover:rotate-6 duration-500", 
            n.modelo === 55 ? "bg-blue-500/10 text-blue-500 border-blue-500/20" : "bg-purple-500/10 text-purple-500 border-purple-500/20"
          )}>
            {n.modelo}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-black truncate max-w-[400px] tracking-tight group-hover:text-primary transition-colors">
              {n.emitente ? (privacyMode ? maskName(n.emitente) : n.emitente) : "UNNAMED ENTITY"}
            </div>
            <div className="text-[10px] font-black text-muted-foreground/60 mt-0.5 tracking-tighter tabular-nums flex items-center gap-1.5">
              <ShieldCheck className="size-3 text-emerald-500" /> {privacyMode ? maskCnpjCpf(n.cnpjEmit) : n.cnpjEmit}
            </div>
          </div>
        </div>
      </td>
      <td className="p-5 border-r border-foreground/[0.03] font-black text-sm tabular-nums tracking-tighter">
        <span className="opacity-40 font-bold mr-1">#</span>{n.numero}<span className="opacity-20 mx-1">/</span>{n.serie}
      </td>
      <td className="p-5 border-r border-foreground/[0.03] text-xs font-black tabular-nums text-muted-foreground/80">
        {fmtDate(n.emissao)}
      </td>
      <td className="p-5 border-r border-foreground/[0.03] text-right font-black text-primary tabular-nums text-lg tracking-tighter">
        {fmtBRL(n.valorOficial)}
      </td>
      <td className="p-5 border-r border-foreground/[0.03]">
        <div className="flex justify-center">
          <StatusBadge status={n.notaStatus} size="sm" />
        </div>
      </td>
      <td className="p-5 text-center">
        <div className="flex justify-center">
          {n.tipoOperacao === "ENTRADA" && (
            <div className="px-4 py-1.5 rounded-xl text-[9px] font-black bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
               ↓ INBOUND
            </div>
          )}
          {n.tipoOperacao === "SAIDA" && (
            <div className="px-4 py-1.5 rounded-xl text-[9px] font-black bg-primary/10 text-primary border border-primary/20 shadow-lg shadow-primary/5">
               ↑ OUTBOUND
            </div>
          )}
          {(!n.tipoOperacao || n.tipoOperacao === "INDEFINIDA") && (
            <div className="px-4 py-1.5 rounded-xl text-[9px] font-black bg-muted/10 text-muted-foreground border border-foreground/[0.05]">
               — UNKNOWN
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

